import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { ParticipantListService } from './participant-list.service';
import { Component, inject, Input, OnInit } from '@angular/core';
import { AlertController, IonicModule } from '@ionic/angular';
import { TournamentHeaderComponent } from '../tournaments/tournament-details/tournament-header/tournament-header.component';
import { ParticipantTypeEnum } from '../_shared/models/csTypes/enums/participant_type';
import { TournamentDetailsService } from '../tournaments/tournament-details/tournament-details.service';
import { ActivatedRoute } from '@angular/router';
import { Tournament } from '../_shared/models/tournament.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { combineLatest, scan, startWith, Subject } from 'rxjs';
import { JsonPipe } from '@angular/common';
import { Participant } from '../_shared/models/participant';

@Component({
  selector: 'app-participant-list',
  templateUrl: './participant-list.component.html',
  styleUrls: ['./participant-list.component.scss'],
  standalone: true,
  // changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonicModule,
    TournamentHeaderComponent,
    ReactiveFormsModule
  ],
})
export class ParticipantListComponent implements OnInit {
  private alertController = inject(AlertController);
  private tournamentDetailsService = inject(TournamentDetailsService);
  private activatedRoute = inject(ActivatedRoute);
  private participantListService = inject(ParticipantListService);
  private fb = inject(FormBuilder);

  @Input() title = 'Players';

  tournament: Tournament;
  usersForm: FormGroup;

  getParticipants$ = new Subject<ParticipantTypeEnum>();
  createdParticipant$ = new Subject<{op: string, participant: Participant}>();
  deletedParticipant$ = new Subject<{op: string, _participantId: string}>();
  filter$ = new Subject<{op: string, value: string}>();

  participants$ = this.getParticipants$.pipe(
    tap(participantType => {
      if (participantType === ParticipantTypeEnum.PAIR) {
        this.users.push(this.createUserGroup())
      } else {
        this.users.removeAt(1)
      }
    }),
    switchMap((participantType) => this.participantListService.getTournamentParticipants(participantType, this.activatedRoute.snapshot.parent.params.id))
  )

  participantsData$ = combineLatest([
    this.participants$,
    this.createdParticipant$.pipe(startWith(null)),
    this.deletedParticipant$.pipe(startWith(null)),
    this.filter$.pipe(startWith(null))
  ]).pipe(
      scan((
          [deletedIds, _],
          [participants, createdParticipant, deletedParticipant, filter]: [
              Participant[],
              { op: string; participant: Participant } | null,
              { op: string; _participantId: string } | null,
              { op: string; value: string } | null
          ]
      ): [string[], Participant[]] => {

        // Add created participant to the bottom of the list
        if (createdParticipant && createdParticipant.op === 'createdParticipant') {
          participants.push(createdParticipant.participant);
          this.usersForm.reset();
          this.createdParticipant$.next(null)
        }

        // Ensure deletedIds is always an array of strings
          deletedIds = deletedIds || [];

          // Update deleted user IDs if there's a deletion
          if (deletedParticipant && deletedParticipant.op === 'deletedParticipant') {
              deletedIds = [...deletedIds, deletedParticipant._participantId];
          }

          // Filter out deleted users from the main data
          participants = participants.filter(participant => !deletedIds.includes(participant._id));

          // Apply additional filtering if there's a filter value
          if (filter && filter.op === 'filter' && filter.value) {
            participants = participants.filter(participant => {
              const nameMatch = (
                  (participant.displayName?.toLowerCase().includes(filter.value.toLowerCase()))
              );
              return nameMatch;
            })
          }

          // Return updated deletedIds and result
          return [deletedIds, participants];
      }, [[], [], []] as [string[], string[], Participant[]]), // Initialize with correct data structure and types

      // Extract the result part for the final output
      map(([, result]) => result),
    );

  participants = toSignal(this.participantsData$, { initialValue: undefined});
  deletingParticipantId: string;

  constructor() {
    this.usersForm = this.fb.group({
      users: this.fb.array([])
    });

    this.users.push(this.createUserGroup())
  }

  // Getter for the FormArray
  get users(): FormArray {
    return this.usersForm.get('users') as FormArray;
  }

  async ngOnInit() {
    const tournamentId = this.activatedRoute.snapshot.parent.params.id;
    this.tournament = await this.tournamentDetailsService.getTournamentInfo(tournamentId);

    this.getParticipants$.next(ParticipantTypeEnum.INDIVIDUAL);
  }

  segmentChanged(ev) {
    this.getParticipants$.next(ev.detail.value);

  }

  onSubmit(participantType: ParticipantTypeEnum) {
    this.participantListService.create(this.users.value, participantType,  this.tournament._id).subscribe(
      participant => this.createdParticipant$.next({ op: 'createdParticipant', participant}),
    )
  }

  createUserGroup(): FormGroup {
    return this.fb.group({
      first_name: [''],
      last_name: ['']
    });
  }

  async deleteParticipant(_participantId: string) {
    const alert = await this.alertController.create({
      header: 'Delete participant',
      message: 'The participant will not be removed from any matches. This action cannot be undone. Are you sure that you want to delete participant?',
      buttons: [{
        text: 'Yes, delete',
        role: 'destructive',
        handler: () => {
          this.deletingParticipantId = _participantId;

          this.participantListService.delete(_participantId).pipe(
            finalize(() => this.deletingParticipantId = null)
          ).subscribe(
            () => this.deletedParticipant$.next({ op: 'deletedParticipant', _participantId })
          )
        }
      }, {
        text: 'Cancel',
        role: 'cancel',
      }
    ]
    });

    await alert.present();
  }

  getItems(ev) {
    // set val to the value of the ev target
    const val = ev.target.value;
    this.filter$.next({op: 'filter', value: val})
  }

}
